import {
  Box,
  Fab,
  Paper,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import logo from '../assets/images/new-logo.svg';
import logoSmall from '../assets/images/new-mobile-logo.svg';
import React from 'react';
import {
  IconBrandDiscordFilled,
  IconBrandLinkedin,
  IconBrandTelegram,
  IconBrandX,
} from '@tabler/icons-react';
import { useAppContext } from '../context/AppContext';

const DesktopLogoStyle: SxProps<Theme> = {
  width: '130px',
  height: 'auto',
  display: {
    xs: 'none',
    md: 'block',
  },
};

const MobileLogoStyle: SxProps<Theme> = {
  width: '140px',
  height: 'auto',
  display: {
    xs: 'block',
    md: 'none',
  },
};

interface Social {
  icon: JSX.Element;
  link: string;
  name: string;
}

interface NavLink {
  link: string;
  name: string;
}

const Footer = () => {
  const theme = useTheme();
  const { setToggleBridgeModal } = useAppContext();

  const socials: Social[] = [
    {
      icon: <IconBrandTelegram />,
      link: 'https://t.me/virtuals',
      name: 'Telegram',
    },
    {
      icon: <IconBrandX />,
      link: 'https://twitter.com/virtuals_io',
      name: 'X',
    },
  ];

  const navLinks: NavLink[] = [
    {
      name: 'Privacy Policy',
      link: '/privacy-policy',
    },
    {
      name: 'Terms and Condition',
      link: '/terms-condition',
    },
    {
      name: 'Treasury Dashboard',
      link: 'http://treasury.virtuals.io/',
    },
    {
      name: 'Virtual Homepage',
      link: 'http://virtuals.io/',
    },
  ];

  const leftNavLinks: NavLink[] = [
    {
      name: 'Virtual Homepage',
      link: 'http://virtuals.io/',
    },
    {
      name: 'Treasury Dashboard',
      link: 'http://treasury.virtuals.io/',
    },
    {
      name: 'Buy Token',
      link: `https://dexscreener.com/base/${process.env.REACT_APP_TOKEN_ADDRESS}`,
    },
    {
      name: 'Bridge Token',
      link: 'http://virtuals.io/',
    },
  ];

  const rightNavLinks: NavLink[] = [
    {
      name: 'Privacy Policy',
      link: '/privacy-policy',
    },
    {
      name: 'Terms and Condition',
      link: '/terms-condition',
    },
  ];

  return (
    <Paper
      square
      style={{
        padding: '2rem',
        borderTop: '1px solid #EFEFEF',
        bottom: 0,
      }}
      sx={{
        zIndex: {
          xs: 1,
          md: 10,
        },
        display: {
          xs: 'block',
          md: 'block',
        },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          justifyContent: {
            xs: 'center',
            md: 'space-between',
          },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{ flexGrow: 1 }}
          display={{ xs: 'flex', md: 'block' }}
          justifyContent={'center'}
          flexDirection={'column'}
          alignItems={'center'}
        >
          <Box component="img" src={logo} alt="logo" sx={DesktopLogoStyle} />
          <Box
            component="img"
            src={logo}
            alt="logo-small"
            sx={MobileLogoStyle}
          />
          <Box sx={{ mt: 2 }}>
            {socials.map((social) => {
              return (
                <Fab
                  size="small"
                  aria-label={social.name}
                  sx={{
                    border: '1px solid #404040',
                    backgroundColor: 'transparent',
                    boxShadow: 0,
                    mr: 1,
                    color: '#404040',
                    zIndex: 0,
                    width: { xs: '36px', md: '40px' },
                    height: { xs: '36px', md: '40px' },
                  }}
                  onClick={() => {
                    window.open(social.link);
                  }}
                >
                  {social.icon}
                </Fab>
              );
            })}
          </Box>
        </Box>
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'flex-start'}
          flexDirection={'row'}
          gap={2}
        >
          <Box
            sx={{ mr: 1 }}
            display={{ xs: 'flex', md: 'none' }}
            justifyContent={'center'}
            flexDirection={'column'}
            alignItems={'center'}
            mt={{ xs: 2, md: 0 }}
          >
            {leftNavLinks.map((nav) => {
              return (
                <Typography
                  sx={{
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'right',
                    ':hover': {
                      textShadow: '0 0 1px black',
                    },
                    mb: 0.5,
                  }}
                  onClick={() => {
                    if (nav.name === 'Bridge Token') {
                      return setToggleBridgeModal(true);
                    }

                    window.open(nav.link);
                  }}
                >
                  {nav.name}
                </Typography>
              );
            })}
          </Box>
          <Box
            sx={{ mr: 1 }}
            display={{ xs: 'flex', md: 'none' }}
            justifyContent={'center'}
            flexDirection={'column'}
            alignItems={'center'}
            mt={{ xs: 2, md: 0 }}
          >
            {rightNavLinks.map((nav) => {
              return (
                <Typography
                  sx={{
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'right',
                    ':hover': {
                      textShadow: '0 0 1px black',
                    },
                    mb: 0.5,
                  }}
                  onClick={() => {
                    window.open(process.env.PUBLIC_URL + nav.link);
                  }}
                >
                  {nav.name}
                </Typography>
              );
            })}
          </Box>
          <Box
            sx={{ mr: 1 }}
            display={{ xs: 'none', md: 'flex' }}
            justifyContent={'center'}
            flexDirection={'column'}
            alignItems={'flex-end'}
            mt={{ xs: 2, md: 0 }}
          >
            {navLinks.map((nav) => {
              return (
                <Typography
                  sx={{
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'right',
                    ':hover': {
                      textShadow: '0 0 1px black',
                    },
                  }}
                  onClick={() => {
                    if (
                      nav.name === 'Privacy Policy' ||
                      nav.name === 'Terms and Condition'
                    ) {
                      window.open(process.env.PUBLIC_URL + nav.link);
                    } else {
                      window.open(nav.link);
                    }
                  }}
                >
                  {nav.name}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Typography
        sx={{ mt: 2, textAlign: 'center', fontSize: '12px', mb: 2, mx: 2 }}
      >
        © {new Date().getFullYear()} Virtuals Protocol All Rights Reserved
      </Typography>
      <Box height={30} display={{ xs: 'block', md: 'none' }}></Box>
    </Paper>
  );
};

export default Footer;
